import { useLayoutEffect } from "react";
import { t } from "../../locales/t";

/**
 * What a browser does for free on a page load, and a single-page app has to do
 * itself on every step change.
 *
 * Three things, all of which are broken by default here:
 *
 *   Focus. Advancing a step unmounts the button that was clicked, so focus
 *   falls back to `<body>`. A keyboard user is returned to the top of the
 *   document and a screen reader announces nothing at all — the new screen
 *   simply never gets read. Moving focus to the content region fixes both.
 *
 *   Scroll. The app scrolls inside one container that survives navigation, so
 *   a new step opens at the previous step's offset unless it is reset. The
 *   reset runs before paint and does not animate, so the new screen is never
 *   seen part-way down.
 *
 *   Title. The tab keeps whatever it said before. Screen readers announce the
 *   title on navigation, and it is what a member sees when they come back to a
 *   backgrounded tab mid-signup.
 *
 * `transitionKey` exists because a step may present several screens without
 * its id changing — the goal sub-flows are five paths under one step. Keyed on
 * the id alone, none of the three things above would happen when a member moves
 * between sub-flows, which is a real navigation. Callers pass something finer;
 * the title still names the step.
 *
 * @param {string|undefined} stepId
 * @param {import('react').RefObject<HTMLElement>} contentRef
 * @param {string} [transitionKey] defaults to the step id
 */
export const useStepTransition = (stepId, contentRef, transitionKey) => {
  const key = transitionKey ?? stepId;

  useLayoutEffect(() => {
    if (!stepId) return;

    const node = contentRef.current;

    // `preventScroll` because the scroll position is set deliberately below;
    // letting focus scroll as well fights it and lands mid-screen.
    node?.focus?.({ preventScroll: true });

    scrollToTop(node);

    document.title = t("onboarding.layout.documentTitle", {
      step: t(`onboarding.steps.${stepId}`),
    });
    // `key` is the trigger; `stepId` only names the title and must not re-fire
    // the effect on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, contentRef]);
};

/**
 * `instant` rather than `auto`, so a `scroll-behavior: smooth` on the container
 * cannot animate the reset.
 *
 * Downgraded on first refusal rather than assumed. `behavior` is a WebIDL enum,
 * so an engine whose `ScrollBehavior` predates `instant` throws a TypeError
 * instead of ignoring the value, and every one of these calls happens in a
 * layout effect above the flow's error boundary: unguarded, the throw blanks the
 * app rather than merely failing to scroll.
 */
let scrollBehavior = "instant";

const resetScroll = (target) => {
  try {
    target.scrollTo({ top: 0, behavior: scrollBehavior });
  } catch {
    // Downgrade once and retry, then give up. A throw here is not proof the
    // enum was the cause, so the retry settles it rather than assuming.
    if (scrollBehavior !== "auto") {
      scrollBehavior = "auto";
      resetScroll(target);
    }
  }
};

/**
 * Scroll whichever ancestor actually scrolls, instantly.
 *
 * The app renders inside a `height: 100vh; overflow: auto` container, so the
 * window never scrolls and `window.scrollTo` alone does nothing. Walking up for
 * the real scrolling ancestor works whether that container stays or the layout
 * changes later.
 */
const scrollToTop = (node) => {
  let current = node?.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const scrolls = overflowY === "auto" || overflowY === "scroll";

    if (scrolls && current.scrollHeight > current.clientHeight) {
      resetScroll(current);
      return;
    }
    current = current.parentElement;
  }

  // No scrolling ancestor: the document itself scrolls.
  resetScroll(window);
};
